"use client";

import { isGlobalRegionSlug } from "@/services/region";

export type SwitchableAccount = {
  id: string;
  name: string;
  email: string;
  isParent: boolean;
  image?: string | null;
};

/** Mirrors the backend's hard cap — see User::MAX_DEPENDANTS_PER_PARENT. */
export const MAX_DEPENDANTS_PER_PARENT = 5;

/**
 * Whether this user is allowed to add another dependant right now: must be a
 * primary account (no parent of its own) and under the dependant cap. The
 * backend re-validates on every create request regardless — this only
 * controls whether the "Add Dependant" affordance is shown.
 */
export function canCreateDependant(user: unknown): boolean {
  if (!isRecord(user)) {
    return false;
  }

  if (user.parent_id != null) {
    return false;
  }

  const dependants = Array.isArray(user.dependants) ? user.dependants : [];

  return dependants.length < MAX_DEPENDANTS_PER_PARENT;
}

export function getSwitchableAccounts(user: unknown): SwitchableAccount[] {
  if (!isRecord(user)) {
    return [];
  }

  const currentId = resolveId(user.id);
  const isPrimaryAccount = user.parent_id == null;

  if (isPrimaryAccount) {
    return normalizeAccounts(user.dependants, currentId, false);
  }

  const parent = isRecord(user.parent) ? buildAccount(user.parent, currentId, true) : null;
  const siblingAccounts = isRecord(user.parent)
    ? normalizeAccounts(user.parent.dependants, currentId, false)
    : [];

  return [parent, ...siblingAccounts].filter((account): account is SwitchableAccount => Boolean(account));
}

/**
 * Sub-user (dependant) accounts have a non-null parent_id. Billing sections
 * (Saved Cards, Payment History) are primary-account-only — mirrors the
 * backend's RestrictSubAccountBilling middleware check.
 */
export function isSubUserAccount(user: unknown): boolean {
  if (!isRecord(user)) {
    return false;
  }

  return user.parent_id != null;
}

/**
 * Sub-users have no independent activation state — they're booking
 * attendees under a parent account, not standalone accounts. Resolves the
 * account whose `is_active` should actually gate access: the primary
 * account itself, or the parent of a switched-into sub-user session.
 */
export function resolvePrimaryAccount(user: unknown): Record<string, unknown> | null {
  if (!isRecord(user)) {
    return null;
  }

  if (user.parent_id != null && isRecord(user.parent)) {
    return user.parent;
  }

  return user;
}

export function isAccountActive(user: unknown): boolean {
  const primaryAccount = resolvePrimaryAccount(user);
  const value = primaryAccount?.is_active;

  return !(value === false || value === 0 || value === "0");
}

/**
 * Whether this account IS a Teacher, i.e. its immutable account_type is
 * "teacher". A teacher account has no student side any more (account contexts
 * are separate accounts, not switchable roles). Used to hide booking entry
 * points (Free Trial, Course enrollment) and Teacher-inapplicable navigation
 * (the public "Home" links in the dashboard) from Teacher accounts.
 */
export function isTeacherAccount(user: unknown): boolean {
  return isRecord(user) && user.account_type === "teacher";
}

/**
 * A teacher who has no single canonical region: either their course-coverage
 * includes the Global region, or they cover more than one region. Such a
 * teacher must NOT be pinned/redirected to a single region by the region
 * guards (a Global teacher belongs to every region, a multi-region teacher to
 * several).
 *
 * Source of truth is `user.regions` — the teacher's region_user coverage
 * pivot, returned by `/api/me` (see AuthService::loadUserRelations()). There
 * is no `is_global` field any more (teacher_profiles.is_global was dropped
 * once Global became an ordinary Region row); Global coverage now shows up as
 * an entry in this same list, identified via isGlobalRegionSlug() exactly
 * like every other Global check in the frontend.
 *
 * Reads the field defensively: when it is absent (e.g. an auth payload that
 * doesn't carry it), this returns false and the existing single-region
 * behavior is preserved unchanged. Students are never teachers, so they are
 * unaffected.
 */
export function isGlobalOrMultiRegionTeacher(user: unknown): boolean {
  if (!isTeacherAccount(user) || !isRecord(user)) {
    return false;
  }

  if (!Array.isArray(user.regions)) {
    return false;
  }

  if (user.regions.length > 1) {
    return true;
  }

  return user.regions.some(
    (region) => isRecord(region) && isGlobalRegionSlug(region.slug as string | undefined)
  );
}

/**
 * Whether this teacher's dashboard should expose the Free Trial Lectures
 * section (the sidebar tab and the /teacher/free-lectures pages).
 *
 * Mirrors `teacher_profiles.show_free_trial_page`, which is admin-controlled
 * from the teacher edit form and defaults to false — so an absent flag (an
 * auth payload without the profile relation, a teacher created before the
 * column existed) keeps the section hidden rather than leaking it.
 *
 * Deliberately NOT derived from free_evaluation_enabled: that flag decides
 * whether the teacher can be booked for a free evaluation, this one only
 * decides what they see.
 */
export function canSeeFreeTrialPage(user: unknown): boolean {
  if (!isTeacherAccount(user) || !isRecord(user)) {
    return false;
  }

  const profile = user.teacher_profile;

  if (!isRecord(profile)) {
    return false;
  }

  const value = profile.show_free_trial_page;

  return value === true || value === 1 || value === "1";
}

/**
 * Whether this account IS a Student, i.e. its immutable account_type is
 * "student". Dependant/profile switching is a student-only feature.
 */
export function isStudentAccount(user: unknown): boolean {
  return isRecord(user) && user.account_type === "student";
}

function normalizeAccounts(value: unknown, currentId: string | null, isParent: boolean): SwitchableAccount[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((account) => (isRecord(account) ? buildAccount(account, currentId, isParent) : null))
    .filter((account): account is SwitchableAccount => Boolean(account));
}

function buildAccount(
  account: Record<string, unknown>,
  currentId: string | null,
  isParent: boolean
): SwitchableAccount | null {
  const id = resolveId(account.id);

  if (!id || id === currentId) {
    return null;
  }

  const rawAvatar = account.avatar as string | null;
  const image = typeof account.image === "string" ? account.image : (rawAvatar ? `/storage/${rawAvatar.replace(/^\//, '')}` : null);

  return {
    id,
    name: resolveName(account),
    email: typeof account.email === "string" ? account.email : "",
    isParent,
    image,
  };
}

function resolveName(account: Record<string, unknown>): string {
  if (typeof account.name === "string" && account.name.trim().length > 0) {
    return account.name;
  }

  const fname = typeof account.fname === "string" ? account.fname : "";
  const lname = typeof account.lname === "string" ? account.lname : "";

  return `${fname} ${lname}`.trim();
}

function resolveId(value: unknown): string | null {
  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
